import React, { useState, useRef, useEffect, useMemo, useDeferredValue, useId } from 'react';
import { Search, X, CheckCircle, XCircle, ChevronDown } from 'lucide-react';

import { UnifiedPatient } from '../../types/unified-financial'; // Usar UnifiedPatient
import { escapeRegExp, getPatientSearchScore } from '../../utils/patientSearch';

const MAX_VISIBLE_SEARCH_RESULTS = 50;

interface PatientSearchShape {
  id?: unknown;
  personalData?: { name?: unknown; cpf?: unknown; rg?: unknown };
  financialProfile?: { active?: unknown };
  patientFullName?: unknown;
  patientCpf?: unknown;
  patientRg?: unknown;
  patientLifecycleStatus?: unknown;
  lifecycleStatus?: unknown;
  nome?: unknown;
  sobrenome?: unknown;
  name?: unknown;
  cpf?: unknown;
  rg?: unknown;
  prontuario?: unknown;
  prontu_x00c1_rio?: unknown;
  patientIdentifier?: unknown;
  patientId?: unknown;
  status?: unknown;
}

export const getPatientName = (patient: unknown): string => {
  const searchablePatient = (patient ?? {}) as PatientSearchShape;
  const personalName = searchablePatient.personalData?.name;
  if (typeof personalName === 'string' && personalName.trim()) return personalName.trim();

  const fullName = searchablePatient.patientFullName;
  if (typeof fullName === 'string' && fullName.trim()) return fullName.trim();

  const composedName = `${searchablePatient.nome ?? ''} ${searchablePatient.sobrenome ?? ''}`.trim();
  if (composedName) return composedName;

  if (typeof searchablePatient.name === 'string' && searchablePatient.name.trim()) {
    return searchablePatient.name.trim();
  }
  return 'Paciente sem nome';
};

export const getPatientSearchIdentifiers = (patient: unknown): string[] => {
  const searchablePatient = (patient ?? {}) as PatientSearchShape;
  const values = [searchablePatient.personalData?.cpf, searchablePatient.personalData?.rg,
    searchablePatient.patientCpf, searchablePatient.patientRg, searchablePatient.cpf, searchablePatient.rg,
    searchablePatient.prontuario, searchablePatient.prontu_x00c1_rio, searchablePatient.patientIdentifier,
    searchablePatient.patientId, searchablePatient.id];
  return [...new Set(values.map(value => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '').filter(Boolean))];
};

export const getPatientIdentifier = (patient: unknown): string => getPatientSearchIdentifiers(patient)[0] ?? '';

const getPatientStatus = (patient: unknown): boolean => {
  const active = (patient as PatientSearchShape | undefined)?.financialProfile?.active;
  if (typeof active === 'boolean') {
    return active;
  }
  return true;
};

const getPatientLifecycleStatus = (patient: unknown): 'active' | 'inactive' | 'archived' => {
  const searchablePatient = (patient ?? {}) as PatientSearchShape;
  const lifecycleStatus = searchablePatient.patientLifecycleStatus ??
    searchablePatient.lifecycleStatus ?? searchablePatient.status;
  if (lifecycleStatus === 'archived') return 'archived';
  if (lifecycleStatus === 'inactive') return 'inactive';
  return 'active';
};

export const isPatientOperationallyActive = (patient: unknown): boolean => (
  Boolean(patient && typeof patient === 'object') && getPatientLifecycleStatus(patient) === 'active' && getPatientStatus(patient)
);

export interface PatientSearchSelectProps {
  patients: UnifiedPatient[]; // Agora aceita UnifiedPatient[]
  selectedPatientId: string;
  onSelect: (patientId: string) => void;
  placeholder?: string;
  showActiveFilter?: boolean;
  allowInactiveSelection?: boolean;
  allowManualOther?: boolean;
  className?: string;
  presentation?: 'dropdown' | 'inline';
}

export default function PatientSearchSelect({
  patients,
  selectedPatientId,
  onSelect,
  placeholder = "Buscar paciente...",
  showActiveFilter = true,
  allowInactiveSelection = true,
  allowManualOther = true,
  className = "",
  presentation = 'dropdown',
}: PatientSearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [isOpen, setIsOpen] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inline = presentation === 'inline';
  const listOpen = inline || isOpen;
  const listId = `${useId()}-patients`;
  const optionRefs = useRef(new Map<number, HTMLButtonElement>());

  const selectedPatient = patients.find(p => p.id === selectedPatientId);

  const sortedPatients = useMemo(() => [...patients].sort((a, b) => {
    const nameA = getPatientName(a);
    const nameB = getPatientName(b);
    return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
  }), [patients]);

  const filteredPatients = useMemo(() => {
    const candidates = sortedPatients
      .filter(patient => {
        if (patient.id === 'all') return !deferredSearchTerm.trim();
        if (getPatientLifecycleStatus(patient) === 'archived') return false;
        if (!allowInactiveSelection && !isPatientOperationallyActive(patient)) return false;
        if (onlyActive && showActiveFilter && !isPatientOperationallyActive(patient)) return false;
        return true;
      })
      .map(patient => ({
        patient,
        score: getPatientSearchScore(
          deferredSearchTerm,
          getPatientName(patient),
          getPatientSearchIdentifiers(patient),
        ),
      }))
      .filter(({ score }) => Number.isFinite(score))
      .sort((a, b) => a.score - b.score || getPatientName(a.patient).localeCompare(
        getPatientName(b.patient),
        'pt-BR',
        { sensitivity: 'base' },
      ))
      .map(({ patient }) => patient);

    return deferredSearchTerm.trim()
      ? candidates.slice(0, MAX_VISIBLE_SEARCH_RESULTS)
      : candidates;
  }, [allowInactiveSelection, deferredSearchTerm, onlyActive, showActiveFilter, sortedPatients]);


  // Adicionar opção "Outro" no final
  const patientsWithOther = allowManualOther
    ? [
        ...filteredPatients,
        {
          id: '__outro__',
          personalData: { name: 'Outro (digitar manualmente)' },
          financialProfile: { active: true, hasPackage: false },
          appointments: [],
          invoices: [],
          createdAt: '',
          updatedAt: '',
        } as UnifiedPatient,
      ]
    : filteredPatients;

  const realPatients = sortedPatients.filter(p => p.id !== 'all' && getPatientLifecycleStatus(p) !== 'archived');
  const activeCount = realPatients.filter(p => getPatientStatus(p)).length;
  const inactiveCount = realPatients.length - activeCount;

  useEffect(() => {
    if (inline) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inline]);

  // The selected-value view does not contain an input. Focus after the open
  // state has mounted it, rather than requiring a second click to start typing.
  useEffect(() => {
    if (isOpen && !inline) inputRef.current?.focus();
  }, [isOpen, inline]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [deferredSearchTerm, onlyActive]);

  useEffect(() => {
    setHighlightedIndex(current => Math.min(current, Math.max(0, patientsWithOther.length - 1)));
  }, [patientsWithOther.length]);

  useEffect(() => {
    if (!listOpen) return;
    optionRefs.current.get(highlightedIndex)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [highlightedIndex, listOpen, filteredPatients]);

  const handleSelect = (patientId: string) => {
    onSelect(patientId);
    if (inline) inputRef.current?.focus();
    else {
      setSearchTerm('');
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!listOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < patientsWithOther.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (patientsWithOther[highlightedIndex]) {
          handleSelect(patientsWithOther[highlightedIndex].id);
        }
        break;
      case 'Escape':
        if (inline) {
          if (searchTerm) {
            e.preventDefault();
            e.stopPropagation();
            setSearchTerm('');
          }
          return;
        }
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const highlightText = (text: string, search: string) => {
    if (!search.trim()) return text;

    const safeSearch = escapeRegExp(search.trim());
    const parts = text.split(new RegExp(`(${safeSearch})`, 'gi'));
    return (
      <span>
        {parts.map((part, index) =>
          part.toLowerCase() === search.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 font-semibold">{part}</mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  };

  return (
    <div ref={dropdownRef} className={`relative ${inline ? 'flex min-h-0 flex-1 flex-col' : ''} ${className}`}>
      <div className={inline ? 'flex min-h-0 flex-1 flex-col gap-2' : 'space-y-2'}>
        {showActiveFilter && (
          <div className="flex shrink-0 items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                Filtrar apenas pacientes ativos
              </span>
              <span className="text-xs text-gray-500">
                ({activeCount} ativos, {inactiveCount} inativos)
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-label="Filtrar apenas pacientes ativos"
              aria-checked={onlyActive}
              onClick={() => setOnlyActive(!onlyActive)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                onlyActive ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  onlyActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}

        <div className={inline ? 'relative flex min-h-0 flex-1 flex-col' : 'relative'}>
          <div
            className={`flex shrink-0 items-center w-full px-3 py-2 border rounded-lg cursor-text transition-all ${
              listOpen
                ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-20'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => {
              setIsOpen(true);
              inputRef.current?.focus();
            }}
            role={selectedPatient && !listOpen ? 'button' : undefined}
            tabIndex={selectedPatient && !listOpen ? 0 : undefined}
            aria-label={selectedPatient && !listOpen ? `Alterar paciente: ${getPatientName(selectedPatient)}` : undefined}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && ['Enter', ' ', 'ArrowDown'].includes(event.key)) {
                event.preventDefault();
                setIsOpen(true);
              }
            }}
          >
            <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />

            {selectedPatient && !listOpen ? (
              <div className="flex min-w-0 flex-1 items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="min-w-0 truncate text-sm" title={getPatientName(selectedPatient)}>
                    {getPatientName(selectedPatient)}
                  </span>
                  {selectedPatient.id !== 'all' && (
                    <>
                      <span className="min-w-0 truncate text-xs text-gray-500" title={getPatientIdentifier(selectedPatient)}>
                        - {getPatientIdentifier(selectedPatient) || selectedPatient.id}
                      </span>
                      {getPatientStatus(selectedPatient) ? (
                        <CheckCircle className="w-3 h-3 shrink-0 text-green-500" />
                      ) : (
                        <XCircle className="w-3 h-3 shrink-0 text-gray-400" />
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-label={placeholder}
                aria-autocomplete="list"
                aria-expanded={listOpen}
                aria-controls={listOpen ? listId : undefined}
                aria-activedescendant={listOpen && filteredPatients.length > 0 && patientsWithOther[highlightedIndex] ? `${listId}-option-${highlightedIndex}` : undefined}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-0 min-w-0 flex-1 outline-none bg-transparent text-sm"
              />
            )}

            {searchTerm && (
              <button
                type="button"
                aria-label="Limpar busca de pacientes"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm('');
                  inputRef.current?.focus();
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            )}

            {!inline && <ChevronDown
              className={`w-4 h-4 text-gray-400 ml-2 transition-transform ${
                listOpen ? 'transform rotate-180' : ''
              }`}
            />}
          </div>

          {listOpen && (
            <div id={listId} role="listbox" aria-label="Pacientes encontrados" className={inline
              ? 'mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-pt-10 rounded-lg border border-gray-200 bg-white'
              : 'absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto scroll-pt-10'}>
              {filteredPatients.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    {deferredSearchTerm
                      ? 'Nenhum paciente encontrado'
                      : onlyActive
                        ? 'Nenhum paciente ativo encontrado'
                        : 'Nenhum paciente cadastrado'
                    }
                  </p>
                  {onlyActive && showActiveFilter && allowInactiveSelection && inactiveCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setOnlyActive(false)}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                    >
                      Mostrar todos os pacientes
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b text-xs text-gray-600 font-medium">
                    {filteredPatients.length} paciente{filteredPatients.length !== 1 ? 's' : ''} encontrado{filteredPatients.length !== 1 ? 's' : ''}
                  </div>
                  {patientsWithOther.map((patient, index) => {
                    const isActive = getPatientStatus(patient);
                    const isHighlighted = index === highlightedIndex;
                    const isSelected = patient.id === selectedPatientId;

                    return (
                      <button
                        key={patient.id}
                        id={`${listId}-option-${index}`}
                        role="option"
                        aria-selected={isSelected}
                        type="button"
                        tabIndex={-1}
                        ref={(element) => {
                          if (element) optionRefs.current.set(index, element);
                          else optionRefs.current.delete(index);
                        }}
                        onMouseDown={inline ? (event) => event.preventDefault() : undefined}
                        onClick={() => handleSelect(patient.id)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={`w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center justify-between transition-colors ${
                          isHighlighted ? 'bg-blue-50' : ''
                        } ${isSelected ? 'bg-blue-100' : ''}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 text-sm font-medium truncate" title={getPatientName(patient)}>
                              {highlightText(
                                getPatientName(patient),
                                deferredSearchTerm
                              )}
                            </span>
                            {isActive ? (
                              <span title="Paciente ativo" className="shrink-0"><CheckCircle className="w-3 h-3 text-green-500" aria-hidden="true" /></span>
                            ) : (
                              <span title="Paciente inativo" className="shrink-0"><XCircle className="w-3 h-3 text-gray-400" aria-hidden="true" /></span>
                            )}
                          </div>
                          <div className="min-w-0 text-xs text-gray-500 flex items-center gap-2">
                            <span className="min-w-0 truncate" title={getPatientIdentifier(patient)}>
                              {highlightText(
                                getPatientIdentifier(patient) || patient.id || 'Sem prontuário',
                                deferredSearchTerm
                              )}
                            </span>
                            {!isActive && (
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                Inativo
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {listOpen && (
        <div className="mt-2 shrink-0 text-xs text-gray-500">
          <span>{inline ? 'Use ↑ ↓ para navegar, Enter para selecionar e Esc para limpar a busca.' : '💡 Dica: Use ↑ ↓ para navegar, Enter para selecionar, Esc para fechar'}</span>
        </div>
      )}
    </div>
  );
}
